import '@/utils/sentry'
import '@/utils/i18n'

import { initializeClient } from '@amarantos/api-client'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import i18n from 'i18next'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { RouteNotFound } from '@/components/route-not-found'
import { getAuthFailureRedirectUrl } from '@/features/auth'
import { reportErrorToSentry } from '@/utils/errors'

import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx'

// Import the generated route tree
import { routeTree } from './routeTree.gen'

import './styles.css'
import reportWebVitals from './report-web-vitals.ts'

initializeClient({
	baseUrl: import.meta.env.VITE_API_BASE_URL,
	withCredentials: true,
	// Without this the browser's own Accept-Language decides the API locale, so an
	// English-UI browser gets English validation errors inside the Polish panel.
	headers: { 'Accept-Language': i18n.resolvedLanguage ?? 'pl' },
	auth: {
		// Routes own these 401s (login errors render inline, password reset is public).
		// '/me' is deliberately NOT skipped: its 401s must hit the silent refresh.
		skipAuthPaths: ['/sessions', '/auth/password/reset'],
		cookieRefresh: { refreshUrl: '/sessions' },
		onAuthFailure: () => {
			const redirectUrl = getAuthFailureRedirectUrl(window.location)
			if (redirectUrl) {
				window.location.href = redirectUrl
			}
		},
	},
})

// Create a new router instance

const TanStackQueryProviderContext = TanStackQueryProvider.getContext()
const router = createRouter({
	routeTree,
	context: {
		...TanStackQueryProviderContext,
	},
	defaultPreload: 'intent',
	scrollRestoration: true,
	defaultStructuralSharing: true,
	defaultPreloadStaleTime: 0,
	defaultErrorComponent: RouteErrorFallback,
	defaultNotFoundComponent: RouteNotFound,
	defaultOnCatch: (error) => reportErrorToSentry(error),
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}

async function enableMocking() {
	if (import.meta.env.VITE_ENABLE_MSW !== 'true') {
		return
	}

	const { worker } = await import('./mocks/browser')
	await worker.start({
		onUnhandledRequest: 'warn',
	})
}

async function bootstrap() {
	await enableMocking()

	// Render the app
	const rootElement = document.getElementById('app')
	if (rootElement && !rootElement.innerHTML) {
		const root = ReactDOM.createRoot(rootElement)
		root.render(
			<StrictMode>
				<TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
					<RouterProvider router={router} />
				</TanStackQueryProvider.Provider>
			</StrictMode>,
		)
	}

	// If you want to start measuring performance in your app, pass a function
	// to log results (for example: reportWebVitals(console.log))
	// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
	reportWebVitals()
}

void bootstrap()
